import React, { useState, useEffect } from "react";
import "../../css/CreateWorkspace.css";
import "../../css/Create.css"
import "../../css/DocumentationMainPage.css"
import "../../css/Account.css"
import { auth } from "../../firebase.js";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"

function CreateWorkspace() {

        const [name, setName] = useState("");
        const [description, setDescription] = useState("");
        const [isPublic, setIsPublic] = useState(false);
        const [customDomain, setCustomDomain] = useState("");
        const [selectedTheme, setSelectedTheme] = useState("Default");
        const [customDomainTitle, setCustomDomainTitle] = useState("");
        const [customDomainEdited, setCustomDomainEdited] = useState(false);
        const [isSubscriber, setIsSubscribor] = useState(false)
        const [customDomainTitleEdited, setCustomDomainTitleEdited] = useState(false);
        const [userUid, setUserUid] = useState(null);
        const [userEmail, setUserEmail] = useState(null);
        const [isCheckingEmail, setIsCheckingEmail] = useState(false);
        const { enhancedRightClickEnabled } = useQuickLinks();
        const [emailExists, setEmailExists] = useState(null);
        const [emailCheckTimeout, setEmailCheckTimeout] = useState(null);

        const [workspaceName, setWorkspaceName] = useState("");
        const [workspaceDescription, setWorkspaceDescription] = useState("");
        const [teamMembers, setTeamMembers] = useState([]);
        const [currentEmail, setCurrentEmail] = useState("");

        const navigate = useNavigate()

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];

    const loggedInOptions = [
            {
                label: "Home",
                icon: <i className="fas fa-home"></i>,
                url: "/",
                onClick: () => navigate("/"),
            },
            {
                label: "Your Documents",
                url: "/documentation",
                onClick: () => navigate("/documentation"),
            },
            {
                label: "Your Walkthroughs",
                url: "/walkthroughs",
                onClick: () => navigate("/walkthroughs"),
            },
            {
                label: "Your Notebook",
                url: "/my-notebook",
                onClick: () => navigate("/my-notebook"),
            },
            {
                label: "Your Workspace",
                url: "/workspaces",
                onClick: () => navigate("/workspaces"),
            },
            {
                label: "Account Settings",
                url: `/account/${userUid}`,
                onClick: () => navigate(`/account/${userUid}`),
            },
        ];

    const menuOptions = userUid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

        const ROLE_OPTIONS = [
                { value: 'ADMIN', label: 'Administrator' },
                { value: 'RW', label: 'Read/Write' },
                { value: 'RO', label: 'Read Only' },
        ];

        useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (user) {
                                setUserUid(user.uid)
                                setUserEmail(user.email)
                        } else {
                                setUserUid(null)
                                setUserEmail(null)
                        }
                })
                return () => { unsubscribe() }
        }, [])

        useEffect(() => {
            if (userUid) {
                fetchAccountDetails();
            }
        }, [userUid]);

        useEffect(() => {
                if (!customDomainTitleEdited) {
                        setCustomDomainTitle(workspaceName);
                }
        }, [workspaceName, customDomainTitleEdited]);

        useEffect(() => {
                setCustomDomain(formatCustomDomain(customDomainTitle));
        }, [customDomainTitle]);

        const checkEmailExists = async (email) => {
                if (!email || !email.includes('@')) {
                        setEmailExists(null);
                        return;
                }
                
                try {
                        setIsCheckingEmail(true);
                        const token = await auth.currentUser.getIdToken();
                        
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/check-email-exists/`, {
                        method: "POST",
                        headers: {
                                'Content-Type': 'application/json',
                                'Auth-Token': `Bearer ${token}`,
                                'Authorization': `${token}`,
                                'Requesting-User-Uid': userUid
                        },
                        body: JSON.stringify({ email })
                        });

                        if (!response.ok) {
                        throw new Error(`Failed to check email: ${response.status}`);
                        }

                        const data = await response.json();
                        setEmailExists(data.exists);
                } catch (error) {
                        console.error("Error checking email:", error);
                        setEmailExists(null);
                } finally {
                        setIsCheckingEmail(false);
                }
        };

        const handleEmailInputChange = (e) => {
        const email = e.target.value;
        setCurrentEmail(email);
        
        // Clear any existing timeout
        if (emailCheckTimeout) {
                clearTimeout(emailCheckTimeout);
        }
        
        // If email is empty, reset the check state
        if (!email) {
                setEmailExists(null);
                return;
        }
        
        // Set a new timeout to check the email after 500ms of inactivity
        const timeout = setTimeout(() => {
                checkEmailExists(email);
        }, 500);
        
        setEmailCheckTimeout(timeout);
        };

        const handleSubmitWorkspace = async (e) => {
                e.preventDefault();
                
                if (!workspaceName) {
                        alert("Please provide a workspace name");
                        return;
                }
                
                if (!customDomain) {
                        alert("Please provide a custom domain");
                        return;
                }
                
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }
                        
                        const workspaceData = {
                                name: workspaceName,
                                description: workspaceDescription,
                                is_public: isPublic,
                                custom_domain: customDomain,
                                custom_domain_title: customDomainTitle,
                                team_members: teamMembers,
                                theme: selectedTheme !== "Default" ? selectedTheme : null
                        };
                        
                        const response = await fetch('https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/create/', {
                                method: 'POST',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'Auth-Token': `Bearer ${token}`,
                                        'Authorization': `${token}`,
                                        'Requesting-User-Uid': userUid
                                },
                                body: JSON.stringify(workspaceData)
                        });
                        
                        const data = await response.json();
                        
                        if (!response.ok) {
                                // Check for the specific unique constraint error
                                if (data.error && data.error.includes("UNIQUE constraint failed") && 
                                    data.error.includes("custom_domain")) {
                                        alert(`The custom domain "${customDomain}" is already taken. Please choose a different domain name.`);
                                } else {
                                        throw new Error(data.error || 'Failed to create workspace');
                                }
                                return;
                        }

                        navigate(`/workspaces`);
                    
                } catch (error) {
                        console.error("Error creating workspace:", error);
                        alert(`Failed to create workspace: ${error.message}`);
                }
        };

        const fetchAccountDetails = async () => {
                        try {
                                const user = auth.currentUser;
                                const token = user ? await user.getIdToken() : null;
                                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${userUid}/`, {
                                        headers: {
                                                'Authorization': `${token}`
                                        }
                                });
                                const data = await response.json();
                                setIsSubscribor(data.is_subscriber);
                        } catch (error) {
                                console.error("Error fetching account details:", error);
                        }
                }

        const formatCustomDomain = (text) => {
                if (!text) return "";    
                return text
                        .toLowerCase()
                        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters except spaces and hyphens
                        .replace(/\s+/g, "-") // Replace spaces with hyphens
                        .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
                        .replace(/^-+|-+$/g, ""); // Remove leading and trailing hyphens
        };
        
        const handleCustomDomainTitleChange = (e) => {
                setCustomDomainTitle(e.target.value);
                setCustomDomainTitleEdited(true);
        };

        const handleAddTeamMember = () => {
        if (!currentEmail || !currentEmail.includes('@')) {
                alert("Please enter a valid email address.");
                return;
        }
        
        if (teamMembers.some(member => member.email === currentEmail)) {
                alert("This email has already been added.");
                return;
        }
        
        setTeamMembers([...teamMembers, { 
                email: currentEmail, 
                role: 'RW', // Default role
                isExistingUser: emailExists // Add this flag
        }]);
        
        setCurrentEmail("");
        setEmailExists(null); // Reset email validation state
        };

        const handleTogglePublic = (e) => {
                setIsPublic(e.target.checked);
        };
        
        const handleRemoveTeamMember = (index) => {
                const updatedMembers = [...teamMembers];
                updatedMembers.splice(index, 1);
                setTeamMembers(updatedMembers);
        };

        const handleRoleChange = (index, newRole) => {
                const updatedMembers = [...teamMembers];
                updatedMembers[index].role = newRole;
                setTeamMembers(updatedMembers);
        };

        const handleThemeChange = (e) => {
            setSelectedTheme(e.target.value);
        };

        const handleHomeButtonClick = () => { navigate("/") }
        const handleLoginButtonClick = () => { navigate("/login") }
        const handleRegisterButtonClick = () => { navigate("/register") }

        if (!userUid) {
                return (
                        <div 
                            className="DocumentationMainPage__div"
                            onContextMenu={
                                enhancedRightClickEnabled
                                ? (e) => {
                                    e.preventDefault();
                                    showContextMenu(e.clientX, e.clientY);
                                    }
                                : undefined
                            }
                        >
                                {/* <Sidebar /> */}
                                <div className="DocumentationMainPage__main-content">
                                        <p className="large">It looks like you're not signed in.</p>
                                        <p className="medium">
                                                <strong>To create a Team Workspace, you must have an account with us</strong>. If you already have an account, you can login by clicking the Login button 
                                                below. If you don't already have an account, you can create a new one by clicking the Register button below.
                                        </p>
                                        <button onClick={handleHomeButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                                        <button onClick={handleLoginButtonClick} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                                        <button onClick={handleRegisterButtonClick} className="ConflictResolver__button">Register</button>
                                </div>
                                {enhancedRightClickEnabled && ContextMenuComponent}
                        </div>
                )
        }

        return (
            <div 
                className="CreateWorkspace__div"
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
            >

                <div className="CreateWorkspace__main-content">

                        <h1>Create a Workspace</h1>
                        <p>
                                Establish a workspace for your organization within Bluebird Documentation; thus, 
                                keeping all your organization documentation in one place.
                        </p>

                        <div className="Create__form-group">
                                <label>Name: </label>
                                <input
                                        type="text"
                                        className="Create__input-short-field"
                                        value={workspaceName}
                                        onChange = {(e) => setWorkspaceName(e.target.value)}
                                        maxLength={150}
                                        placeholder="Let's give this awesome workspace a name!"
                                        required
                                />
                        </div>

                        <div className="Create__form-group">
                                <label>Custom Domain Name: </label>
                                <input
                                        type="text"
                                        className="Create__input-short-field"
                                        value={customDomainTitle}
                                        onChange={handleCustomDomainTitleChange}
                                        maxLength={150}
                                        required
                                />
                        </div>
                        <div className="Create__form-group-helper-text">
                                <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                <p className="Create__helper-text">
                                        Your new Team Workspace will essentially exist as a sub 
                                        domain to the base Bluebird Documentation domain. It 
                                        doesn't have to be the same as the workspace name; that's 
                                        just the default.
                                </p>
                        </div>

                        <p className="extra-small">
                                https://bluebird-documentation.com/workspace/{customDomain || formatCustomDomain(customDomainTitle)}
                        </p>

                        <div className="Create__form-group">
                                <label>Description: </label>
                                <input
                                        type="text"
                                        className="Create__input-full-field"
                                        value={workspaceDescription}
                                        onChange = {(e) => setWorkspaceDescription(e.target.value)}
                                />
                        </div>
                        <div className="Create__form-group-helper-text">
                                <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                <p className="Create__helper-text">
                                        The description is only used to provide a gist of what this 
                                        workspace is about to anyone that you invite.
                                </p>
                        </div>

                        {/* <div className="Create__form-group">
                                <label>Make this workspace public?</label>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={isPublic}
                                                onChange={handleTogglePublic}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>
                        <div className="Create__form-group-helper-text">
                                <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                <p className="Create__helper-text">
                                        Public workspaces can be discovered by anyone, but access to content is still 
                                        controlled by team member permissions.
                                </p>
                        </div> */}

                        <div className="Create__form-group" style={{ flexDirection: "column", marginBottom: "60px", marginTop: "30px" }}>
                                <label>Invite Team Members</label>
                                <div className="Create__form-group-helper-text">
                                        <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                        <p className="Create__helper-text">
              To learn more about group privlages, please visit <a 
                href="https://bluebird-documentation.com/documentation/page/Workspace%20Permissions/NGzFPNeV9GY1g6q2PFX7SfQmRqw1" 
                target="_blank" 
                rel="noopener noreferrer">this link</a>. 
                                        </p>
                                </div>
                                <p className="CreateWorkspace__user-note-text">
                                        If the email you input isn't associated with an existing user, they'll need to make an 
                                        account first before they can access this workspace.
                                </p>

                                {teamMembers.map((member, index) => (
                                <div 
                                        key={index} 
                                        className={`Create__email-item ${member.isExistingUser === false ? 'non-existing-user' : ''}`}
                                        style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}
                                >
                                        <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {member.email}
                                        {member.isExistingUser === false && (
                                                <span className="user-status-badge">Not a user</span>
                                        )}
                                        </span>
                                        <select
                                        value={member.role}
                                        className="Create__select"
                                        onChange={(e) => handleRoleChange(index, e.target.value)}
                                        style={{ marginLeft: '10px', padding: '5px' }}
                                        >
                                        {ROLE_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>
                                                {option.label}
                                                </option>
                                        ))}
                                        </select>
                                        <button 
                                        onClick={() => handleRemoveTeamMember(index)} 
                                        style={{ marginLeft: '10px', background: 'none', border: 'none', cursor: 'pointer' }}
                                        >
                                        <i className="fas fa-trash-alt" title="Remove Team Member" style={{ color: "white" }}></i>
                                        </button>
                                </div>
                                ))}

                                <div className="Create__email-input-container">
                                        <input
                                                type="email"
                                                className="Create__email-input"
                                                placeholder="Enter Team Member's Email"
                                                value={currentEmail}
                                                onChange={handleEmailInputChange}
                                                onKeyDown={(e) => e.key === "Enter" && handleAddTeamMember()}
                                        />
                                        {isCheckingEmail && (
                                                <span className="email-validation-icon" title="Checking email">
                                                <i className="fas fa-spinner fa-spin"></i>
                                                </span>
                                        )}
                                        {!isCheckingEmail && emailExists === true && (
                                                <span className="email-validation-icon valid-email" title="User exists">
                                                <i className="fas fa-check-circle"></i>
                                                </span>
                                        )}
                                        {!isCheckingEmail && emailExists === false && (
                                                <span className="email-validation-icon invalid-email" title="User does not exist">
                                                <i className="fas fa-exclamation-circle"></i>
                                                </span>
                                        )}
                                        <button 
                                                onClick={handleAddTeamMember} 
                                                className="Create__add-email-btn"
                                                disabled={isCheckingEmail}
                                        >
                                                <i className="fas fa-plus"></i>
                                        </button>
                                </div>
                        </div>

                        {isSubscriber && (
                            <>
                                <div className="Create__form-group" style={{ marginTop: "20px", flexDirection: "column" }}>
                                    <label style={{ marginBottom: "10px" }}>Workspace Theme: </label>
                                    <select
                                        className="form-select Account__input"
                                        aria-label="Default select example"
                                        name="account_theme"
                                        value={selectedTheme}
                                        onChange={handleThemeChange}
                                        required
                                    >
                                        <option value="Default">Default (No Theme)</option>
                                        <option value="Dark Theme">Dark Theme</option>
                                        <option value="Purple Theme">Purple Theme</option>
                                    </select>
                                </div>
                                <div className="Create__form-group-helper-text" style={{ marginTop: "15px" }}>
                                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                    <p className="Create__helper-text">
                                        As a subscriber, you can customize the appearance of your workspace.
                                    </p>
                                </div>
                            </>
                        )}
                        <button onClick={handleSubmitWorkspace} className="submit-button">Create Workspace</button>

                </div>

                {enhancedRightClickEnabled && ContextMenuComponent}

            </div>
        );

}

export default CreateWorkspace;